
  import { defineComponent, onMounted, ref, computed, nextTick } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import * as Yup from 'yup';
  import jsPDF from 'jspdf';
  import autoTable from 'jspdf-autotable';
  import { getUser } from '@/core/services/AuthService';
  import LookupNameIds from '@/store/enums/settingsEnums';
  import { injectSearch } from '@/core/plugins/search-service';
  import SearchField from '@/components/common/SearchField.vue';
  import moment from 'moment';
  import { loadImage } from '../../../utils/loadImage';

  export default defineComponent({
    name: 'horses-report',
    components: {
      ErrorMessage,
      Field,
      Form,
      SearchField,
    },
    async setup() {
      // Excel Export variables
      const jsonExcel = ref();
      const excelData = ref<any[]>([]);
      const excelHeader = computed(() => ({
        Location: 'Location',
        'Sr.': 'Sr.',
        Horse: 'Horse',
        'Horse Names': 'Horse Names',
      }));

      // Excel Export function
      const printExcel = async () => {
        try {
          store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-extra');
          const values = {
            horseId: selectedHorse.value || 0,
            horseTypeId: selectedHorseType.value || 0,
            horseLocationParentId: selectedHorseLocationParentId.value || 0,
          };
          const report = await store.dispatch(
            Actions.GET_HORSE_LOCATION_DETAILS,
            values
          );
          const resTabelBody: Array<{
            Location: string;
            'Sr.': number | string;
            Horse: string;
            'Horse Names': string;
          }> = [];
          if (report && report.data) {
            report.data.forEach((location) => {
              location.horseLocations.forEach((loc, idx) => {
                resTabelBody.push({
                  Location: location.name,
                  'Sr.': idx + 1,
                  Horse: loc.name,
                  'Horse Names': loc.horseNames,
                });
              });
              // Add total count row after each location, only in first column
              resTabelBody.push({
                Location: `==== TOTAL COUNT: ${location.horseLocations.length} ====`,
                'Sr.': '',
                Horse: '',
                'Horse Names': '',
              });
            });
          }
          excelData.value = resTabelBody;
          await nextTick();
          if (jsonExcel.value && resTabelBody.length > 0) {
            jsonExcel.value.generate();
            jsonExcel.value.$el.style.display = 'none';
          } else if (resTabelBody.length === 0) {
            alert('No data available for export');
          }
        } catch (error) {
          console.error('Error exporting Excel:', error);
          alert('Error exporting data. Please try again.');
        } finally {
          store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-extra');
        }
      };
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const selectedItem = ref<number>(0);
      const loadingPage = ref(true);
      const searchService = injectSearch();
      const reportData = ref();
      const selectedCustomer = ref<number>(0);
      const selectedTrainer = ref<number>(0);
      const selectedHorse = ref<number>(0);
      const selectedAttendance = ref<number>(0);
      const selecteDuration = ref<number>(0);
      const selecteType = ref<number>(0);
      const selectedHorseType = ref<number>(0);
      const selectedDay = ref<number>(0);
      const selectedHorseLocationParentId = ref<number>(0);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-extra');
      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('HorsesReport'), [
          translate('reports'),
        ]);
        store.dispatch(Actions.GET_ALL_HORSES_OPERATIONS);
        const getSaveData = searchService.getSearchValue('HorsesReport');
        const dataSaved =
          Object.keys(getSaveData).length > 0
            ? JSON.parse(searchService.getSearchValue('HorsesReport'))
            : {};
        if (Object.keys(getSaveData).length > 0) {
          if (dataSaved.horseId) selectedHorse.value = dataSaved.horseId;
          if (dataSaved.horseTypeId)
            selectedHorseType.value = dataSaved.horseTypeId;
        }
        if (dataSaved.horseLocationParentId) {
          selectedHorseLocationParentId.value = dataSaved.horseParentId;
        }
        loadingPage.value = false;
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-extra');
      });
      const clearSearch = () => {
        searchService.removeSearchValue('HorsesReport');
        selectedCustomer.value = 0;
        selectedHorse.value = 0;
        selectedHorseType.value = 0;
        selectedHorseLocationParentId.value = 0;
      };

      const print = async (values) => {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-extra');

        searchService.setSearchValue('HorsesReport', values);

        const user = getUser();
        const userName = user ? user['name'] : '';

        const reportData = await store.dispatch(
          Actions.GET_HORSE_LOCATION_DETAILS,
          values
        );

        const doc: any = new jsPDF({ format: 'a4', orientation: 'portrait' });

        const pageWidth = doc.internal.pageSize.getWidth();
        const tableMargin = 20;
        const availableWidth = pageWidth - tableMargin * 2;

        const columnStyles = {
          0: { halign: 'center', cellWidth: 15 },
          1: { halign: 'left', cellWidth: availableWidth },
        };
        const fontSize = 10;
        doc.setFontSize(fontSize);

        let textX =
          (doc.internal.pageSize.getWidth() -
            doc.getTextWidth('Kuwait Riding Center')) /
          2;

        const dataLength = reportData.data.length;
        if (dataLength === 0) {
          doc.text('No Result Found', 100, 70);
        }

        let pageCount = 0;

        reportData.data.forEach((report, reportIndex) => {
          if (reportIndex > 0) {
            doc.addPage();
          }

          doc.text(`Location: ${report.name}`, 13, 45);
          doc.setFont('helvetica', 'normal');

          const tableBody = report.horseLocations.map((loc) => [
            loc.name,
            loc.horseNames,
          ]);

          autoTable(doc, {
            head: [['Sr.', 'Horse']],
            body: tableBody,
            startY: 50,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            margin: { horizontal: 'auto', top: 40 },
            theme: 'plain',
            headStyles: {
              fillColor: [180, 255, 180],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              halign: 'left',
            },
            bodyStyles: { fontStyle: 'normal', halign: 'left' },
            styles: { fontSize: 10 },
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            columnStyles: columnStyles,
            didDrawCell: function (data) {
              if (data.section === 'head') {
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.2);
                doc.rect(
                  data.cell.x,
                  data.cell.y,
                  data.cell.width,
                  data.cell.height
                );
              }
            },
            didDrawPage: function () {
              doc.setFontSize(10);
              pageCount++;
              const pageCountWidth = doc.getTextWidth(String(pageCount));
              doc.text(
                'Page ' + pageCount,
                textX - pageCountWidth,
                doc.internal.pageSize.height - 9
              );
              doc.setFontSize(fontSize);
            },
          });
          const finalY = doc.lastAutoTable.finalY + 3;
          doc.setLineDash([1, 1]);
          doc.line(20, finalY, pageWidth - 20, finalY);
          doc.setLineDash([]);
          doc.setFont('helvetica', 'bold');
          doc.text(
            `totalCount: ${report.horseLocations.length}`,
            20,
            finalY + 5
          );
          doc.setFont('helvetica', 'normal');
        });

        const logoImage: any = await loadImage('/media/logos/empty-krc.png');

        Array.from({ length: pageCount }, (x, i) => {
          doc.setFontSize(9);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text('Kuwait Riding Center', textX, 15);
          doc.setFontSize(12);
          doc.text('Horse Location Details', textX - 5, 22);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text('Print DateTime: ' + moment(new Date()).format('LLL'), 5, 8);
          doc.addImage(logoImage, 'png', 5, 3, 35, 35);
          doc.setPage(i + 1);
          doc.text(userName, 5, doc.internal.pageSize.height - 10);
          doc.text(
            ' of ' + doc.getNumberOfPages(),
            textX + 10,
            doc.internal.pageSize.height - 9
          );
          doc.setFontSize(fontSize);
        });

        const data = doc.output('blob');
        const blobUrl = URL.createObjectURL(data);
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-extra');
        window.open(blobUrl, '_blank');
      };

      const validationSchema = Yup.object().shape({});

      await store.dispatch(Actions.GET_ALL_SETTINGS_LOOKUPS, [
        LookupNameIds.DAYS,
        LookupNameIds.CLASS_TYPE,
        LookupNameIds.DURATION,
        LookupNameIds.STATUS,
        LookupNameIds.ATTENDEND,
        LookupNameIds.HORSE_TYPE,
      ]);
      await store.dispatch(Actions.GET_ALL_SETTINGS_HORSE_PARENT_LOCATIONS);

      const lookupsList = computed(() => store.getters.allLookupsList);

      const statusList = lookupsList.value.filter((lookup) => {
        return lookup.lookupNameId === LookupNameIds.ATTENDEND;
      });
      const classType = lookupsList.value.filter((lookup) => {
        return lookup.lookupNameId === LookupNameIds.CLASS_TYPE;
      });

      const dayList = lookupsList.value.filter((lookup) => {
        return lookup.lookupNameId === LookupNameIds.DAYS;
      });

      const durationList = lookupsList.value.filter((lookup) => {
        return lookup.lookupNameId === LookupNameIds.DURATION;
      });
      const horseTypeList = lookupsList.value.filter((lookup) => {
        return lookup.lookupNameId === LookupNameIds.HORSE_TYPE;
      });

      return {
        printExcel,
        excelData,
        excelHeader,
        jsonExcel,
        selectedItem,
        submitButton,
        translate,
        validationSchema,
        goBack,
        can,
        print,
        reportData,
        customers: computed(() => store.getters.allCustomersList),
        trainers: computed(() => store.getters.allTrainersList),
        horses: computed(() => store.getters.allHorsesList),
        allHorseParentList: computed(() => store.getters.allHorseParentList),
        statusList,
        classType,
        // categoryList,
        dayList,
        durationList,
        selectedCustomer,
        selectedTrainer,
        selectedHorse,
        selecteDuration,
        selectedAttendance,
        selecteType,
        selectedDay,
        selectedHorseType,
        selectedHorseLocationParentId,
        horseTypeList,
        loadingPage,
        clearSearch,
      };
    },
  });
